import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { DonneesClassement, recupererClassement } from '../game/api';
import { formatTemps } from '../game/logic';
import { BrancheFlorale } from '../components/Florals';

export default function Classement({ grand = false }: { grand?: boolean }) {
  const [donnees, setDonnees] = useState<DonneesClassement | null>(null);
  const [erreur, setErreur] = useState(false);

  useEffect(() => {
    let actif = true;
    const charger = async () => {
      const d = await recupererClassement();
      if (!actif) return;
      if (d) {
        setDonnees(d);
        setErreur(false);
      } else {
        setErreur(true);
      }
    };
    charger();
    const t = window.setInterval(charger, grand ? 4000 : 6000);
    return () => {
      actif = false;
      window.clearInterval(t);
    };
  }, [grand]);

  const scores = donnees?.scores || [];

  if (grand) {
    return (
      <div className="min-h-screen bg-white flex flex-col" data-testid="classement-grand-ecran">
        <header className="px-12 pt-10 flex items-end justify-between">
          <div>
            <h1 className="font-script text-encre text-7xl leading-none">Le classement</h1>
            <p className="petit-cap mt-3 text-base tracking-[0.3em]">
              Château de Saint-Senoch · 22 août 2026
            </p>
          </div>
          <div className="flex items-center gap-3 pb-2">
            <span className="w-3.5 h-3.5 rounded-full bg-succes point-direct" />
            <span className="text-encre-clair font-medium text-xl uppercase tracking-widest">En direct</span>
          </div>
        </header>
        <main className="flex-1 px-12 py-8">
          {!donnees && !erreur ? (
            <p className="text-encre-clair text-3xl mt-16 text-center">Chargement du classement…</p>
          ) : scores.length === 0 ? (
            <p className="text-encre-clair text-3xl mt-16 text-center">
              {erreur ? 'Classement momentanément injoignable…' : 'Aucun temps enregistré pour l\'heure — la course est ouverte.'}
            </p>
          ) : (
            <ol className="space-y-3" data-testid="classement-liste">
              {scores.slice(0, 12).map((s) => (
                <li
                  key={s.rang}
                  className={`flex items-center gap-8 px-8 py-4 rounded-lg border ${
                    s.rang <= 3 ? 'border-encre bg-brume' : 'border-brume bg-white'
                  }`}
                >
                  <span
                    className={`w-16 h-16 shrink-0 rounded-full flex items-center justify-center text-4xl font-semibold tabular-nums ${
                      s.rang === 1 ? 'bg-encre text-white' : 'text-encre border border-encre'
                    }`}
                  >
                    {s.rang}
                  </span>
                  <span className="flex-1 text-encre text-5xl font-medium truncate">{s.pseudo}</span>
                  {s.penalitesSec > 0 && (
                    <span className="text-erreur text-2xl font-medium">+{Math.round(s.penalitesSec / 60)} min</span>
                  )}
                  <span className="text-encre text-5xl font-semibold tabular-nums">{formatTemps(s.tempsSec)}</span>
                </li>
              ))}
            </ol>
          )}
        </main>
        <footer className="px-12 pb-8 flex items-center justify-between">
          <BrancheFlorale className="w-64" />
          <p className="text-encre-clair text-lg">
            {donnees ? `${donnees.total} arrivé${donnees.total > 1 ? 's' : ''}` : ''} · actualisé toutes les 4 s
          </p>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="classement-mobile">
      <header className="sticky top-0 bg-white border-b border-brume z-10">
        <div className="max-w-md mx-auto px-5 h-14 flex items-center justify-between">
          <Link to="/" data-testid="retour-jeu-btn" className="min-h-[44px] flex items-center gap-1 text-encre-clair font-medium text-sm">
            <ChevronLeft size={18} /> Retour au jeu
          </Link>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-succes point-direct" />
            <span className="petit-cap">En direct</span>
          </div>
        </div>
      </header>
      <main className="max-w-md mx-auto px-5 py-6 pb-16">
        <h1 className="font-script text-encre text-5xl">Le classement</h1>
        <p className="petit-cap mt-2">Château de Saint-Senoch</p>

        {!donnees && !erreur ? (
          <p className="text-encre-clair mt-10 text-center leading-relaxed">Chargement du classement…</p>
        ) : scores.length === 0 ? (
          <p className="text-encre-clair mt-10 text-center leading-relaxed">
            {erreur
              ? 'Classement momentanément injoignable. Nouvel essai dans quelques secondes…'
              : "Aucun temps enregistré pour l'heure — la course est ouverte."}
          </p>
        ) : (
          <ol className="mt-6 space-y-2" data-testid="classement-liste">
            {scores.map((s) => (
              <li
                key={s.rang}
                className={`flex items-center gap-4 px-4 py-3 rounded-lg border ${
                  s.rang <= 3 ? 'border-encre bg-brume' : 'border-brume bg-white'
                }`}
              >
                <span
                  className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-sm font-semibold tabular-nums ${
                    s.rang === 1 ? 'bg-encre text-white' : 'text-encre border border-encre'
                  }`}
                >
                  {s.rang}
                </span>
                <span className="flex-1 text-encre font-medium truncate">{s.pseudo}</span>
                {s.penalitesSec > 0 && (
                  <span className="text-erreur text-xs font-medium">+{Math.round(s.penalitesSec / 60)}′</span>
                )}
                <span className="text-encre font-semibold tabular-nums">{formatTemps(s.tempsSec)}</span>
              </li>
            ))}
          </ol>
        )}
        <div className="flex justify-center mt-12">
          <BrancheFlorale className="w-48" />
        </div>
      </main>
    </div>
  );
}
